import { AddCircleOutlineOutlined } from '@mui/icons-material';
import { SecondaryButton, useModal } from '@shape-ds/components';
import PermissionedButton from 'components/PermissionedButton/PermissionedButton';
import { CASES_PAGE } from 'config/paths';
import { snackbarType } from 'hooks/useGlobalNotification';
import {
  useCaseRelatedAlarms,
  useInvalidateCaseAlarmsQueries,
  useInvalidateCasesQueries,
  useUpdateCaseMutation,
} from 'queries/cases/queries';
import { CaseForm, CaseListItem, caseStatus, Severity, severityOptions } from 'queries/cases/types';
import { EventItem, NextStep } from 'queries/events/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'ui/GenericModal/Modal';
import useCaseModal from '../../hooks/useCaseModal';
import { useCaseColumns } from '../../hooks/useCaseColumns';
import { useSnackbar } from '../../hooks/useSnackbar';
import { CaseListTable } from './Table';

interface Props {
  data: CaseListItem[];
  event: EventItem;
  nextStep?: NextStep;
}

export const severityMap = {
  Information: severityOptions.low,
  Warning: severityOptions.low,
  Minor: severityOptions.medium,
  Major: severityOptions.high,
  Critical: severityOptions.very_high,
};

export const ListCases = ({ data, event, nextStep }: Props) => {
  const { t } = useTranslation();
  const { closeAll, closeModal, openModal } = useModal();
  const { openCaseModal } = useCaseModal();

  const attachedAlarm = event.id.includes('-CLONE') ? event?.children ?? [] : [event];

  const handleCreate = () => {
    closeModal();
    openCaseModal(
      {
        status: caseStatus.diagnosis,
        name: event.description,
        asset: event.asset,
        alarms: attachedAlarm,
        severity: severityMap[event.severity as string as keyof typeof severityMap] as Severity,
        anomaly_start: event.alarm_datetime_start,
      } as CaseForm,
      {
        onClose: closeAll,
      },
    );
  };

  useEffect(() => {
    if (nextStep === NextStep.CREATE) return handleCreate();
  }, [nextStep]);

  const { defaultSnackbar } = useSnackbar();

  const [selectedCase, setSelectedCase] = useState<CaseListItem>();
  const { data: relatedAlarmsData, isFetching } = useCaseRelatedAlarms({ id: selectedCase?.id });
  const { alarms } = relatedAlarmsData || {};

  const invalidateCases = useInvalidateCasesQueries();
  const invalidateCaseAlarms = useInvalidateCaseAlarmsQueries();

  const onRequestSuccess = () => {
    defaultSnackbar();
  };

  const onRequestError = () => {
    defaultSnackbar({ type: snackbarType.error });
  };

  const updateCaseMutation = useUpdateCaseMutation(onRequestSuccess, onRequestError);

  const handleSave = () => {
    if (selectedCase) {
      updateCaseMutation.mutate(
        {
          asset_id: selectedCase.asset?.id,
          id: selectedCase.id,
          alarms:
            alarms
              ?.concat(attachedAlarm)
              .map((alarm) => ({ id: alarm.id, is_automatic: false, name: alarm.name })) ?? [],
        },
        {
          onSuccess: () => {
            invalidateCases(selectedCase?.id);
            invalidateCaseAlarms(selectedCase?.id);
            window.open(CASES_PAGE(selectedCase.id), '_blank');
            closeAll();
          },
        },
      );
    }
  };

  const handleCancel = () => {
    openModal(
      <Modal
        title={t('case:messages.attach_exit_without_saving.title')}
        footer={{
          className: 'is-justify-content-flex-end',
          primary: {
            text: t('case:messages.attach_exit_without_saving.button.primary'),
            onClick: closeAll,
          },
          secondary: {
            text: t('case:messages.attach_exit_without_saving.button.secondary'),
          },
        }}
      />,
      { onClose: closeAll },
    );
  };

  const { columns } = useCaseColumns(false, [
    'severity',
    'start_date',
    'name',
    'dimension',
    'status',
    'action_status',
  ]);

  return (
    <>
      <CaseListTable data={data} columns={columns} handleClick={setSelectedCase} />

      <div className="is-flex is-justify-content-flex-end mt-2">
        <SecondaryButton onClick={handleCancel}>{t('case:common.cancel')}</SecondaryButton>

        <PermissionedButton
          buttonVariant="secondary"
          className="mx-3"
          onClick={handleCreate}
          startIcon={<AddCircleOutlineOutlined />}
        >
          {t('case:buttons.newCase')}
        </PermissionedButton>

        <PermissionedButton disabled={!selectedCase || isFetching} onClick={handleSave}>
          {t('case:common.save')}
        </PermissionedButton>
      </div>
    </>
  );
};
